import { ReactElement } from 'react';
import { useCurrentFrame } from 'remotion';

// Fonts
import { lalezarFontFamily } from '@/styles/fonts';

// Constants
import { VIDEO_CONFIG } from '@/constants';

interface ScrollingTextRowProps {
  text: string;
  /** Direction of movement: 'left' means moving left-to-right, 'right' means moving right-to-left */
  direction: 'left' | 'right';
  /** Vertical position (top offset) */
  top: number;
  /** Speed of scrolling in pixels per frame */
  speed: number;
  /** Font size in pixels */
  fontSize: number;
  /** Text color */
  color: string;
}

export const ScrollingTextRow = ({
  text,
  direction,
  top,
  speed,
  fontSize,
  color,
}: ScrollingTextRowProps): ReactElement => {
  const frame = useCurrentFrame();
  const { width } = VIDEO_CONFIG;

  // Calculate the width of one text block (approximate based on font size and text length)
  const textWidth = text.length * fontSize * 0.75;
  const gap = fontSize * 0.35; // Gap between repeated texts
  const blockWidth = textWidth + gap;

  // Number of repetitions needed to fill the screen
  // Adding extra to ensure seamless looping
  const repetitions = Math.ceil(width / blockWidth) + 3;

  // Calculate offset based on frame and direction
  const offset = (frame * speed) % blockWidth;
  const xOffset = direction === 'left' ? offset : -offset;

  return (
    <div
      className="absolute flex whitespace-nowrap"
      style={{
        top,
        left: -blockWidth, // Start off-screen for seamless loop
        transform: `translateX(${xOffset}px)`,
      }}
    >
      {Array.from({ length: repetitions }).map((_, index) => (
        <span
          key={index}
          className="uppercase opacity-20 leading-none"
          style={{
            fontSize,
            color,
            marginRight: gap,
            fontFamily: lalezarFontFamily,
          }}
        >
          {text}
        </span>
      ))}
    </div>
  );
};
